import { useTranslation } from 'react-i18next'

import type { CampaignRow } from '#/entities/campaign'
import { Button } from '#/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '#/shared/ui/dialog'

/**
 * Deleting a campaign, confirmed.
 *
 * The delete is a soft one on the service, but it takes the sequence's
 * enrollments and its send history off every screen that reads them, so the
 * dialog names the campaign rather than asking about "this item" — the menu it
 * came from sits on a table where every row looks alike.
 */
export function CampaignDeleteDialog({
  campaign,
  busy,
  onConfirm,
  onCancel,
}: {
  campaign: CampaignRow | null
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  return (
    <Dialog
      open={campaign !== null}
      onOpenChange={(next) => !next && onCancel()}
    >
      <DialogContent className="max-w-100">
        <div className="flex flex-col gap-4 p-5">
          <div className="flex flex-col gap-1.5">
            <DialogTitle>{t('campaigns.delete.title')}</DialogTitle>
            <DialogDescription>
              {t('campaigns.delete.body', { name: campaign?.name ?? '' })}
            </DialogDescription>
          </div>
          <div className="flex justify-end gap-2.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              pressEffect
              disabled={busy}
              onClick={onCancel}
            >
              {t('campaigns.delete.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              pressEffect
              autoFocus
              disabled={busy}
              onClick={onConfirm}
            >
              {t('campaigns.delete.confirm')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
